import { contentApi } from './content.api';

/**
 * The agent library, as two more endpoints on the one slice — the same cache, the
 * same envelope unwrap and the same bearer token, as `plugins.api.ts` does for its
 * own service.
 *
 * **These types are declared here and again in the service, on purpose.** The
 * service's type says what it emits and this one says what the client expects, and
 * collapsing them removes the only place a mismatch could be written down across
 * a boundary whose halves deploy independently. See the service's ADR 0002.
 */

/** A method an agent is linked to. `id` is the Confluence page id; `path` is what a link needs. */
export type AgentMethodLink = { id: string; name: string; path: string };

/**
 * What an evaluation case says, **not what a run of it produced**: the input, and
 * what a good output looks like. Nothing runs these yet, so a page that labelled
 * `expectedOutput` as "Output" would be claiming a result that does not exist.
 */
export type AgentEvaluation = {
  id: number;
  name: string;
  input: string;
  fixtures: string[];
  expectedOutput: string;
  expectations: string[];
};

/** An agent the toolkit ships, or a prompt imported from Prompt Library. */
export type AgentKind = 'agent' | 'prompt';

export type AgentVersion = {
  name: string;
  version: string;
  title: string;
  summary: string;
  prompt: string;
  methods: AgentMethodLink[];
  /** Absent on a version written before prompts existed, which were all agents. */
  kind?: AgentKind;
  source: 'repository' | 'prompt-library';
  /** For an agent, its file in the repository. For a prompt, its address in Prompt Library. */
  sourcePath: string;
  evaluations: AgentEvaluation[];
};

/** A card: the newest version of an agent, and how many there are. */
export type AgentSummary = {
  name: string;
  title: string;
  summary: string;
  version: string;
  versionCount: number;
  methods: AgentMethodLink[];
  kind: AgentKind;
};

/** One agent and every version of it, **newest first**. */
export type AgentDetail = {
  name: string;
  title: string;
  latestVersion: string;
  versions: AgentVersion[];
};

export const agentsApi = contentApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Every agent as a card. Small enough that the method page filters it
     * itself rather than asking for its own, the way search filters the catalog.
     */
    listAgents: builder.query<AgentSummary[], void>({
      query: () => '/content/agents',
      transformResponse: (response: { agents: AgentSummary[] }) => response.agents,
    }),

    /** One agent with every version, each carrying its prompt and evaluations. */
    getAgent: builder.query<AgentDetail, string>({
      query: (name) => `/content/agents/${encodeURIComponent(name)}`,
      transformResponse: (response: { agent: AgentDetail }) => response.agent,
    }),
  }),
});

export const { useListAgentsQuery, useGetAgentQuery } = agentsApi;
